/**
 * SkillPointController — persistent singleton that receives SP cost and
 * recovery notifications incrementally from DerivedEventController.
 *
 * Pure receiver: never scans the event array. DerivedEventController calls
 * addCost / addRecovery per event or frame. After all events are processed,
 * finalize() sorts accumulated events, feeds them to the SP subtimeline,
 * computes insufficiency zones, and notifies UltimateEnergyController about
 * natural SP consumption per battle skill.
 */
import { TimelineEvent, durationSegment } from '../../consts/viewTypes';
import { NounType } from '../../dsl/semantics';
import type { TimeStopRegion } from '../timeline/processTimeStop';
import { Subtimeline } from '../timeline/subtimeline';
import {
  SkillPointTimeline,
  ResourceZone,
  SkillPointConsumptionHistory,
  SP_COST_EVENT_ID,
  SP_RECOVER_EVENT_ID,
  SP_RETURN_EVENT_ID,
} from '../timeline/skillPointTimeline';
import { ResourceGraphListener, ResourcePoint } from '../timeline/resourceTimeline';
import { TEAM_ID, COMMON_COLUMN_IDS } from './commonSlotController';
import type { UltimateEnergyController } from '../timeline/ultimateEnergyController';

export type { ResourceZone, SkillPointConsumptionHistory };
export { SP_COST_EVENT_ID, SP_RECOVER_EVENT_ID, SP_RETURN_EVENT_ID };

export class SkillPointController {
  private readonly subtimeline: Subtimeline;
  private readonly timeline: SkillPointTimeline;

  /** Per-slot SP insufficiency zones, keyed by `slotId:battle`. */
  insufficiencyZones: Map<string, ResourceZone[]> = new Map();

  /** Accumulated SP events (costs and recoveries) during pipeline. */
  private pendingSpEvents: TimelineEvent[] = [];

  /** Map of battle skill event UID → { frame, ownerEntityId } for UE ultimate energy gain placement. */
  private battleSkillGainFrames = new Map<string, { frame: number; slotId: string }>();

  /** Per-slot SP cost tracking (for insufficiency zones). */
  private slotSpCosts = new Map<string, number>();

  /** Reference to UE controller for natural SP consumption notification. */
  private ueController: UltimateEnergyController | null = null;

  constructor() {
    this.subtimeline = new Subtimeline(TEAM_ID, COMMON_COLUMN_IDS.SKILL_POINTS);
    this.timeline = new SkillPointTimeline(this.subtimeline);
    // On every SP graph change, rebuild the per-slot insufficiency zones
    // map AND push natural-SP consumption updates to UE controller. Both
    // flow reactively from addCost/addRecovery — no finalize step.
    this.timeline.onGraphChange(() => {
      this._recomputeInsufficiencyZones();
      this._notifyUeNaturalConsumption();
    });
  }

  private _notifyUeNaturalConsumption() {
    if (!this.ueController) return;
    for (const record of this.timeline.consumptionHistory) {
      this.ueController.onNaturalSpConsumed(record);
    }
  }

  private _recomputeInsufficiencyZones() {
    const next = new Map<string, ResourceZone[]>();
    this.slotSpCosts.forEach((cost, slotId) => {
      const zones = this.timeline.insufficiencyZones(cost);
      if (zones.length > 0) next.set(`${slotId}:${NounType.BATTLE}`, zones);
    });
    this.insufficiencyZones = next;
  }

  // ── Delegated resource properties ───────────────────────────────────────

  get min() { return this.timeline.min; }
  get max() { return this.timeline.max; }
  get consumptionHistory(): SkillPointConsumptionHistory[] { return this.timeline.consumptionHistory; }
  get wastedSP() { return this.timeline.wastedSP; }

  getSubtimeline(): Subtimeline { return this.subtimeline; }
  getGraph(): ReadonlyArray<ResourcePoint> { return this.timeline.getGraph(); }
  /** Natural-only SP graph (excludes returned SP). The gap between this
   *  and getGraph() represents returned SP at each frame. */
  getNaturalGraph(): ReadonlyArray<ResourcePoint> { return this.timeline.naturalGraph; }
  onGraphChange(listener: ResourceGraphListener): () => void { return this.timeline.onGraphChange(listener); }
  valueAt(frame: number): number { return this.timeline.valueAt(frame); }

  // ── Configuration ──────────────────────────────────────────────────────

  updateConfig(config: { startValue?: number; max?: number; regenPerFrame?: number }): void {
    this.timeline.updateConfig(config);
  }

  setUltimateEnergyController(ue: UltimateEnergyController) {
    this.ueController = ue;
  }

  /**
   * Receive the current time-stop ranges from DEC._maybeRegisterStop.
   * The caller passes the complete set each call; the SP timeline
   * recomputes regen pauses against the latest stops.
   */
  setTimeStops(stops: readonly TimeStopRegion[]) {
    this.timeline.setTimeStops(
      stops.map(s => ({ startFrame: s.startFrame, endFrame: s.startFrame + s.durationFrames })),
    );
  }

  /**
   * Seed per-slot SP costs for insufficiency zone computation.
   * Called before the pipeline so the reactive insufficiency-zones cache
   * covers ALL slots, not just those with battle skill events. Triggers a
   * recompute so any prior graph state already projects against the new
   * slot set.
   */
  seedSlotCosts(costs: ReadonlyMap<string, number>) {
    costs.forEach((cost, slotId) => this.slotSpCosts.set(slotId, cost));
    this._recomputeInsufficiencyZones();
  }

  // ── Accumulation methods (called by DerivedEventController) ─────────────

  /**
   * Called per battle skill event with an SP cost.
   * @param eventUid Original event UID (for UE natural SP tracking).
   * @param frame The frame at which SP is consumed.
   * @param amount SP cost amount.
   * @param ownerEntityId Slot that owns the battle skill.
   * @param ultimateEnergyGainFrame Frame where UE ultimate energy gain should be placed for this BS.
   */
  addCost(eventUid: string, frame: number, amount: number, ownerEntityId: string, ultimateEnergyGainFrame: number) {
    this.pendingSpEvents.push({
      uid: `${eventUid}-sp`,
      id: SP_COST_EVENT_ID,
      name: SP_COST_EVENT_ID,
      ownerEntityId: TEAM_ID,
      columnId: COMMON_COLUMN_IDS.SKILL_POINTS,
      startFrame: frame,
      segments: durationSegment(amount),
    });
    this.battleSkillGainFrames.set(eventUid, { frame: ultimateEnergyGainFrame, slotId: ownerEntityId });
    // Track per-slot SP cost for insufficiency zones
    if (!this.slotSpCosts.has(ownerEntityId)) {
      this.slotSpCosts.set(ownerEntityId, amount);
    }
    // Push the battle skill gain frame to UE so the SP → UE conversion
    // runs reactively without a finalize-time gainFrames param.
    if (this.ueController) {
      this.ueController.setBattleSkillGainFrame(eventUid, ultimateEnergyGainFrame, ownerEntityId);
    }
    this.flushSpEvents();
  }

  /**
   * Called per frame or event that recovers/returns SP.
   *
   * The two verbs are engine-distinct:
   *  - RECOVER (isReturn=false) → natural SP pool (yellow fill). Battle skills
   *    consuming this SP later generate ultimate energy.
   *  - RETURN  (isReturn=true)  → returned SP pool (red fill). Consumed first
   *    on subsequent SP costs and does NOT contribute to ultimate energy.
   *
   * @param frame Absolute frame of the recovery.
   * @param amount SP amount recovered (positive).
   * @param sourceEntityId Owner of the skill that produced this recovery.
   * @param sourceSkillId Skill name that produced this recovery.
   * @param isReturn True if this is a RETURN refund; false for natural RECOVER.
   */
  addRecovery(
    frame: number,
    amount: number,
    sourceEntityId: string,
    sourceSkillId: string,
    isReturn: boolean = false,
  ) {
    if (amount <= 0) return;
    const eventId = isReturn ? SP_RETURN_EVENT_ID : SP_RECOVER_EVENT_ID;
    this.pendingSpEvents.push({
      uid: `${eventId}-${frame}-${this.pendingSpEvents.length}`,
      id: eventId,
      name: eventId,
      ownerEntityId: TEAM_ID,
      columnId: COMMON_COLUMN_IDS.SKILL_POINTS,
      startFrame: frame,
      segments: [{ properties: { duration: amount } }],
      sourceEntityId,
      sourceSkillId,
    });
    this.flushSpEvents();
  }

  /**
   * Called per SP recovery event (already derived by the interpret pipeline).
   */
  addSpRecoveryEvent(ev: TimelineEvent) {
    this.pendingSpEvents.push(ev);
    this.flushSpEvents();
  }

  /**
   * Push current pendingSpEvents into the subtimeline, sorted with
   * cost-before-return tiebreaker at the same frame. Called on every
   * addCost/addRecovery so the SP graph is always current — no separate
   * finalize step needed for graph building.
   */
  private flushSpEvents() {
    this.pendingSpEvents.sort(
      (a, b) => a.startFrame - b.startFrame || (a.name === SP_COST_EVENT_ID ? -1 : 1),
    );
    this.subtimeline.setEvents(this.pendingSpEvents);
  }

  // ── Clear (called at pipeline start) ────────────────────────────────────

  /**
   * Reset accumulated state for a new pipeline run. Called once per run
   * before the drain begins. Empties the subtimeline eagerly so that a run
   * with zero battle-skill events (e.g. a fresh loadout) doesn't inherit
   * the previous run's SP consumption graph — later addCost/addRecovery
   * calls will repopulate it via flushSpEvents.
   */
  clearPending() {
    this.pendingSpEvents = [];
    this.battleSkillGainFrames = new Map();
    this.slotSpCosts = new Map();
    this.subtimeline.setEvents([]);
  }

  clear(): void {
    this.subtimeline.clear();
    this.clearPending();
  }

  destroy(): void {
    this.timeline.destroy();
  }

/** Get battle skill gain frames map (for UE finalize). */
  getBattleSkillGainFrames(): ReadonlyMap<string, { frame: number; slotId: string }> {
    return this.battleSkillGainFrames;
  }
}
